import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { VdiApprovalService } from '../../services/vdi-approval.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, takeUntil } from 'rxjs';
import { DatePipe } from '@angular/common';

export interface RdpConnection {
  id: string;
  ticketId: number;
  vmName: string;
  publicIp: string;
  privateIp: string;
  adminUsername: string;
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  startTime?: Date;
  endTime?: Date;
  sessionUrl?: string;
  error?: string;
}

@Component({
  selector: 'app-rdp-client',
  templateUrl: './rdp-client.component.html',
  styleUrls: ['./rdp-client.component.scss']
})
export class RdpClientComponent implements OnInit, OnDestroy {
  @Input() approval: any;
  @Input() azureVmStatus: any;
  @Input() disabled: boolean = false;
  @Output() connectionStateChanged = new EventEmitter<RdpConnection>();

  // RDP connection state
  connecting: boolean = false;
  connection: RdpConnection | null = null;
  activeSessionWindow: any = null;

  // UI state
  selectedMethod: 'web' | 'native' | 'azure' = 'web';
  showAdvancedOptions: boolean = false;

  // Command options for advanced section
  selectedCommand: string = '';
  commandOptions = [
    { label: 'PowerShell', value: 'powershell' },
    { label: 'Command Prompt', value: 'cmd' },
    { label: 'Linux/macOS', value: 'linux' }
  ];

  // Security
  maxSessionDuration: number = 4 * 60 * 60 * 1000; // 4 hours
  sessionCheckInterval: any = null;

  private destroy$ = new Subject<void>();

  constructor(
    private vdiApprovalService: VdiApprovalService,
    private message: NzMessageService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    console.log('🖥️ [RDP Client] Component initialized', {
      vmName: this.approval?.vmName,
      publicIp: this.approval?.publicIpAddress,
      isRunning: this.azureVmStatus?.isRunning
    });
  }

  ngOnDestroy(): void {
    this.cleanupSession();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Check if RDP connection is available
   */
  get canConnectRdp(): boolean {
    return !!(
      this.approval &&
      this.approval.vmCreationStatus === 'Succeeded' &&
      this.azureVmStatus?.isRunning &&
      this.approval.publicIpAddress &&
      this.approval.adminUsername &&
      !this.connecting &&
      !this.disabled
    );
  }

  /**
   * Get connection status color
   */
  get connectionStatusColor(): string {
    if (!this.connection) return 'default';

    switch (this.connection.status) {
      case 'connecting': return 'processing';
      case 'connected': return 'success';
      case 'disconnected': return 'default';
      case 'error': return 'error';
      default: return 'default';
    }
  }

  /**
   * Get connection status text
   */
  get connectionStatusText(): string {
    if (!this.connection) return 'Not Connected';

    switch (this.connection.status) {
      case 'connecting': return 'Connecting...';
      case 'connected': return 'Connected';
      case 'disconnected': return 'Disconnected';
      case 'error': return this.connection.error || 'Connection Failed';
      default: return 'Unknown Status';
    }
  }

  /**
   * Connect using selected method
   */
  connectRdp(): void {
    if (!this.canConnectRdp) {
      this.message.error('RDP connection not available');
      return;
    }

    console.log(`🖥️ [RDP Client] Connecting via ${this.selectedMethod} method`);

    switch (this.selectedMethod) {
      case 'web':
        this.connectWebRdp();
        break;
      case 'native':
        this.connectNativeRdp();
        break;
      case 'azure':
        this.connectAzureRdp();
        break;
      default:
        this.connectNativeRdp();
    }
  }

  /**
   * Connect via web-based RDP (Guacamole/Azure Bastion)
   */
  private connectWebRdp(): void {
    this.connecting = true;
    this.initializeConnection('web');

    // Generate web RDP session URL
    this.vdiApprovalService.getRdpSessionUrl(this.approval.ticketId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ [RDP Client] Web RDP session created:', response);
          this.connecting = false;
          this.openWebRdpSession(response.rdpUrl);
        },
        error: (error) => {
          console.error('❌ [RDP Client] Failed to create web RDP session:', error);
          this.connecting = false;
          this.handleConnectionError('Failed to create web RDP session. Please try native RDP.');
        }
      });
  }

  /**
   * Open web RDP session in new window
   */
  private openWebRdpSession(rdpUrl: string): void {
    const windowFeatures = 'width=1200,height=800,scrollbars=yes,resizable=yes,toolbar=no,menubar=no';
    this.activeSessionWindow = window.open(rdpUrl, 'rdp_session_' + Date.now(), windowFeatures);

    if (this.activeSessionWindow) {
      this.updateConnectionStatus('connected');
      if (this.connection) {
        this.connection.sessionUrl = rdpUrl;
      }

      // Monitor window closure
      this.sessionCheckInterval = setInterval(() => {
        if (this.activeSessionWindow.closed) {
          this.handleSessionEnd();
        }
      }, 1000);

      this.message.success('Web RDP session opened in new window');
    } else {
      this.handleConnectionError('Failed to open RDP window. Check popup blocker settings.');
    }
  }

  /**
   * Connect via native RDP (download .rdp file or copy command)
   */
  private connectNativeRdp(): void {
    console.log('🖥️ [RDP Client] Initiating native RDP connection');
    this.initializeConnection('native');

    // Show connection dialog
    this.showNativeRdpDialog();
  }

  /**
   * Show native RDP connection options
   */
  private showNativeRdpDialog(): void {
    const rdpCommands = [
      {
        label: 'Download .rdp file',
        action: () => this.downloadRdpFile(),
        icon: 'download'
      },
      {
        label: 'Copy RDP command',
        action: () => this.copyRdpCommand(),
        icon: 'copy'
      },
      {
        label: 'Copy connection details',
        action: () => this.copyConnectionDetails(),
        icon: 'file-text'
      }
    ];

    // For simplicity, we'll just download the .rdp file
    this.downloadRdpFile();
    this.updateConnectionStatus('connected');

    this.message.info('Native RDP connection initiated. Check your downloads or clipboard.');
  }

  /**
   * Download .rdp configuration file
   */
  private downloadRdpFile(): void {
    if (!this.approval?.publicIpAddress || !this.approval?.adminUsername) {
      this.message.error('VM connection details not available');
      return;
    }

    const rdpContent = this.generateRdpFileContent();
    const blob = new Blob([rdpContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `VM-${this.approval.vmName || this.approval.ticketId}.rdp`;
    a.style.display = 'none';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);

    console.log('📁 [RDP Client] RDP file downloaded:', a.download);
  }

  /**
   * Generate RDP file content
   */
  private generateRdpFileContent(): string {
    const config = {
      'full address': `s:${this.approval.publicIpAddress}`,
      'screen mode id': 'i:2',
      'use multimon': 'i:1',
      'session bpp': 'i:32',
      'authentication level': 'i:2',
      'username': `s:${this.approval.adminUsername}`,
      'desktopwidth': 'i:1920',
      'desktopheight': 'i:1080',
      'audiomode': 'i:0',
      'redirectdrives': 'i:1',
      'redirectprinters': 'i:1',
      'smart sizing': 'i:1'
    };

    return Object.entries(config)
      .map(([key, value]) => `${key}:${value}`)
      .join('\n');
  }

  /**
   * Copy RDP command to clipboard
   */
  private copyRdpCommand(): void {
    const command = `mstsc /v:${this.approval.publicIpAddress}`;

    navigator.clipboard.writeText(command).then(() => {
      this.message.success('RDP command copied to clipboard');
      console.log('📋 [RDP Client] RDP command copied:', command);
    }).catch(() => {
      this.message.error('Failed to copy command to clipboard');
    });
  }

  /**
   * Copy connection details to clipboard
   */
  private copyConnectionDetails(): void {
    const details = `
VDI Connection Details:
Computer: ${this.approval.publicIpAddress}
Username: ${this.approval.adminUsername}
VM Name: ${this.approval.vmName}
Private IP: ${this.approval.privateIpAddress}

Connection Methods:
1. Web RDP: Use the "Connect via Web RDP" button
2. Native RDP: Run "mstsc /v:${this.approval.publicIpAddress}"
3. Azure Portal: Access through Azure Portal > Virtual Machines > Connect
    `.trim();

    navigator.clipboard.writeText(details).then(() => {
      this.message.success('Connection details copied to clipboard');
      console.log('📋 [RDP Client] Connection details copied');
    }).catch(() => {
      this.message.error('Failed to copy details to clipboard');
    });
  }

  /**
   * Connect via Azure Portal/Azure Bastion
   */
  private connectAzureRdp(): void {
    console.log('☁️ [RDP Client] Connecting via Azure');
    this.initializeConnection('azure');

    const azurePortalUrl = this.generateAzurePortalUrl();
    window.open(azurePortalUrl, '_blank');

    this.updateConnectionStatus('connected');
    this.message.info('Azure Portal opened in new tab. Navigate to VM and click Connect.');
  }

  /**
   * Generate Azure Portal URL for VM connection
   */
  private generateAzurePortalUrl(): string {
    // This would need your subscription ID, resource group, etc.
    // For now, we'll open the general Azure Portal
    return 'https://portal.azure.com/#blade/HubsExtension/BrowseResource/resourceType/Microsoft.Compute%2FvirtualMachines';
  }

  /**
   * Initialize RDP connection
   */
  private initializeConnection(method: string): void {
    this.connection = {
      id: this.generateConnectionId(),
      ticketId: this.approval.ticketId,
      vmName: this.approval.vmName,
      publicIp: this.approval.publicIpAddress,
      privateIp: this.approval.privateIpAddress,
      adminUsername: this.approval.adminUsername,
      status: 'connecting',
      startTime: new Date()
    };

    this.connectionStateChanged.emit(this.connection);
  }

  /**
   * Update connection status
   */
  private updateConnectionStatus(status: 'connected' | 'disconnected' | 'error', error?: string): void {
    if (this.connection) {
      this.connection.status = status;
      if (error) {
        this.connection.error = error;
      }
      this.connectionStateChanged.emit(this.connection);
    }
  }

  /**
   * Handle connection error
   */
  private handleConnectionError(error: string): void {
    this.updateConnectionStatus('error', error);
    this.message.error(error);
    console.error('❌ [RDP Client] Connection error:', error);
  }

  /**
   * Handle session end
   */
  private handleSessionEnd(): void {
    console.log('🏁 [RDP Client] RDP session ended');
    this.cleanupSession();
    this.updateConnectionStatus('disconnected');
    this.message.info('RDP session ended');
  }

  /**
   * Disconnect active RDP session
   */
  disconnectRdp(): void {
    if (this.activeSessionWindow && !this.activeSessionWindow.closed) {
      this.activeSessionWindow.close();
    }

    this.handleSessionEnd();
  }

  /**
   * Clean up session resources
   */
  private cleanupSession(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }

    if (this.activeSessionWindow) {
      this.activeSessionWindow = null;
    }
  }

  /**
   * Generate unique connection ID
   */
  private generateConnectionId(): string {
    return `rdp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Format connection duration
   */
  formatDuration(startTime?: Date): string {
    if (!startTime) return '0:00:00';

    const now = new Date();
    const duration = now.getTime() - startTime.getTime();

    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((duration % (1000 * 60)) / 1000);

    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Check if session should be terminated (duration limit)
   */
  private checkSessionDuration(): void {
    if (this.connection?.startTime) {
      const elapsed = Date.now() - this.connection.startTime.getTime();
      if (elapsed > this.maxSessionDuration) {
        this.message.warning('Session time limit reached. Disconnecting...');
        this.disconnectRdp();
      }
    }
  }

  /**
   * Get connection icon based on method and status
   */
  getConnectionIcon(): string {
    if (this.connecting) {
      return 'loading';
    }

    if (this.selectedMethod === 'web') {
      return 'global';
    } else if (this.selectedMethod === 'azure') {
      return 'cloud';
    } else {
      return 'desktop';
    }
  }

  /**
   * Get connect button text based on method
   */
  getConnectButtonText(): string {
    if (this.connecting) {
      return 'Connecting...';
    }

    switch (this.selectedMethod) {
      case 'web':
        return 'Connect via Web RDP';
      case 'native':
        return 'Generate RDP File';
      case 'azure':
        return 'Open Azure Portal';
      default:
        return 'Connect';
    }
  }

  /**
   * Get unavailable message when RDP is not accessible
   */
  getUnavailableMessage(): string {
    if (!this.approval) {
      return 'No VDI approval information available';
    }

    if (this.approval.vmCreationStatus !== 'Succeeded') {
      return `VM creation is ${this.approval.vmCreationStatus || 'not started'}`;
    }

    if (!this.azureVmStatus?.isRunning) {
      return 'VM is not currently running. Please wait for it to start.';
    }

    if (!this.approval.publicIpAddress) {
      return 'VM does not have a public IP address assigned';
    }

    return 'RDP connection is not available at this time';
  }

  /**
   * Check if this is an Azure VM
   */
  get isAzureVm(): boolean {
    return !!(
      this.approval?.vmName?.startsWith('avd-') ||
      this.approval?.vmName?.startsWith('vm-') ||
      this.approval?.vmName?.toLowerCase().includes('azure')
    );
  }

  /**
   * Handle command selection in advanced options
   */
  onCommandSelect(option: any): void {
    const commands = this.getRdpCommands();
    this.selectedCommand = commands[option.value] || '';
  }

  /**
   * Get RDP commands for different platforms
   */
  private getRdpCommands(): { [key: string]: string } {
    if (!this.approval?.publicIpAddress) {
      return {};
    }

    return {
      powershell: `mstsc /v:${this.approval.publicIpAddress} /admin`,
      cmd: `mstsc /v:${this.approval.publicIpAddress}`,
      linux: `xfreerdp /v:${this.approval.publicIpAddress} /u:${this.approval.adminUsername || 'username'}`
    };
  }

  /**
   * Copy text to clipboard
   */
  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.message.success('Copied to clipboard');
    }).catch(() => {
      this.message.error('Failed to copy to clipboard');
    });
  }

  /**
   * Show help dialog
   */
  showHelp(topic: string): void {
    const helpContent = this.getHelpContent(topic);

    // You could implement a modal or navigate to help page
    this.message.info(`Help: ${topic}`);
    console.log('📖 [RDP Client] Help topic:', topic, helpContent);
  }

  /**
   * Get help content for different topics
   */
  private getHelpContent(topic: string): string {
    const helpContent: { [key: string]: string } = {
      troubleshooting: `
Troubleshooting Steps:
1. Ensure VM is running (check status above)
2. Verify you have network access to the public IP
3. Check firewall settings on the VM
4. Make sure RDP is enabled on the VM
5. Try different connection methods
      `,
      security: `
Security Information:
- All RDP sessions are logged for audit purposes
- Sessions automatically disconnect after 4 hours
- Use strong passwords for your VM
- Keep your VM and software updated
- Report any suspicious activity to IT support
      `,
      contact: `
Contact Support:
- Email: helpdesk@vtc.edu.hk
- Phone: +852 1234 5678
- Create a support ticket through the portal
- Live chat available during business hours
      `
    };

    return helpContent[topic] || 'Help content not available.';
  }
}